import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCountriesForForm } from '../utils/countries';
import SubscriptionModal from './SubscriptionModal';
import WelcomeBackModal from './WelcomeBackModal';
import { useUserAuth } from '../contexts/UserAuthContext';
import config from '../config';

function NewPrayer({ onSubmit }) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useUserAuth();
  const [formData, setFormData] = useState({
    email: '',
    nombre: '',
    comentario: '',
    pais: '',
    visible: true
  });

  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showWelcomeBackModal, setShowWelcomeBackModal] = useState(false);
  const [submittedPrayer, setSubmittedPrayer] = useState(null);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [userStats, setUserStats] = useState({});
  const [subscriptionPreferences, setSubscriptionPreferences] = useState(null);
  
  // Estados para autocomplete
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const [autofilledMessage, setAutofilledMessage] = useState('');
  
  // Estado para envío del formulario
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll hacia arriba al montar el componente
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Función para obtener datos del usuario desde localStorage
  const getUserDataFromCache = (email) => {
    try {
      const cached = localStorage.getItem('prayer_user_data');
      if (cached) {
        const data = JSON.parse(cached);
        if (data.email === email) {
          return data;
        }
      }
    } catch (error) {
      console.error('Error leyendo cache:', error);
    }
    return null;
  };

  // Función para guardar datos en localStorage
  const saveUserDataToCache = (email, nombre, pais) => {
    try {
      const data = { email, nombre, pais, timestamp: Date.now() };
      localStorage.setItem('prayer_user_data', JSON.stringify(data));
    } catch (error) {
      console.error('Error guardando cache:', error);
    }
  };

  // Función para buscar datos del usuario por email
  const fetchUserDataByEmail = useCallback(async (email) => {
    if (!email || !email.includes('@')) {
      return;
    }

    // Primero intentar desde cache (localStorage)
    const cachedData = getUserDataFromCache(email);
    if (cachedData) {
      console.log('✨ Datos cargados desde cache');
      setFormData(prev => ({
        ...prev,
        nombre: cachedData.nombre,
        pais: cachedData.pais
      }));
      setAutofilledMessage(`¡Hola ${cachedData.nombre}! Hemos rellenado tus datos. Puedes editarlos si han cambiado.`);
      return;
    }

    // Si no hay cache, consultar al backend
    setIsLoadingUserData(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/prayers/user-data/${encodeURIComponent(email)}`);
      const data = await response.json();

      if (data.success && data.found) {
        console.log('✅ Datos cargados desde servidor');
        setFormData(prev => ({
          ...prev,
          nombre: data.data.nombre,
          pais: data.data.pais
        }));
        setAutofilledMessage(`¡Hola ${data.data.nombre}! Hemos rellenado tus datos. Puedes editarlos si han cambiado.`);
        
        // Guardar en cache para la próxima vez
        saveUserDataToCache(email, data.data.nombre, data.data.pais);
      }
    } catch (error) {
      console.error('Error obteniendo datos del usuario:', error);
    } finally {
      setIsLoadingUserData(false);
    }
  }, []);

  // Effect para autocompletar cuando cambia el email (con debounce)
  useEffect(() => {
    // Limpiar mensaje si el email cambia
    if (formData.email === '') {
      setAutofilledMessage('');
    }

    // Si el email es válido, esperar 800ms antes de buscar
    if (formData.email && formData.email.includes('@')) {
      const timer = setTimeout(() => {
        fetchUserDataByEmail(formData.email);
      }, 800);

      // Cleanup: limpiar timer al desmontar o cuando cambie el email
      return () => {
        clearTimeout(timer);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.email, fetchUserDataByEmail]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevenir múltiples envíos
    if (isSubmitting) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Guardar datos en cache antes de enviar
      saveUserDataToCache(formData.email, formData.nombre, formData.pais);
      
      // Llamar a onSubmit que viene desde App.js
      const result = await onSubmit(formData);
      
      // Si la respuesta incluye datos del pedido y estadísticas del usuario
      if (result && result.success) {
        // Limpiar formulario y mensaje
        setFormData({ 
          email: '', 
          nombre: '', 
          comentario: '', 
          pais: '', 
          visible: true 
        });
        setAutofilledMessage('');
        
        // Decidir si mostrar modal según suscripción existente
        if (result.hasActiveSubscription) {
          // Usuario ya tiene suscripción activa, mostrar modal de bienvenida
          console.log('✅ Usuario con suscripción activa, mostrando modal de bienvenida');
          
          setSubmittedPrayer({
            id: result.data.id,
            email: formData.email,
            nombre: formData.nombre,
            comentario: formData.comentario
          });
          
          setUserStats(result.userStats || {});
          setSubscriptionPreferences(result.subscriptionPreferences || {});
          
          // Mostrar modal de bienvenida para usuarios suscritos
          setShowWelcomeBackModal(true);
        } else {
          // Usuario sin suscripción, mostrar modal
          setSubmittedPrayer({
            id: result.data.id,
            email: formData.email,
            nombre: formData.nombre,
            comentario: formData.comentario
          });
          
          setIsReturningUser(result.isReturningUser || false);
          setUserStats(result.userStats || {});
          
          // Mostrar modal de suscripción
          setShowSubscriptionModal(true);
        }
      }
      
    } catch (error) {
      console.error('Error al enviar pedido:', error);
      // El error ya se maneja en App.js
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleCloseModal = (shouldRedirect = false) => {
    setShowSubscriptionModal(false);
    setSubmittedPrayer(null);
    setIsReturningUser(false);
    setUserStats({});
    
    // Redirigir al dashboard después de suscribirse
    if (shouldRedirect) {
      navigate('/');
    }
  };

  const handleCloseWelcomeBackModal = (shouldRedirect = false) => {
    setShowWelcomeBackModal(false);
    setSubmittedPrayer(null);
    setUserStats({});
    setSubscriptionPreferences(null);
    
    // Redirigir al dashboard
    if (shouldRedirect) {
      navigate('/');
    }
  };

  return (
    <div className="new-prayer">
      <h2>Hacer un Pedido de Oración</h2>
      
      {isAuthenticated && user && (
        <div className="new-prayer-user-info">
          Enviando como <strong>{user.nombre || user.email}</strong>
        </div>
      )}

      <div className="prayer-form">
        {/* Only show identity fields for anonymous users */}
        {!isAuthenticated && (
          <>
            <div className="form-group">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="tu.email@ejemplo.com"
              />
              {isLoadingUserData && (
                <div className="autocomplete-loading">
                  <span className="loading-spinner"></span> Buscando tus datos...
                </div>
              )}
              {autofilledMessage && !isLoadingUserData && (
                <div className="autocomplete-message">
                  {autofilledMessage}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Nombre Completo *</label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                required
                placeholder="Tu nombre completo"
              />
            </div>

            <div className="form-group">
              <label>País *</label>
              <select
                name="pais"
                value={formData.pais}
                onChange={handleChange}
                required
              >
                <option value="">Selecciona tu país</option>
                {getCountriesForForm().map(country => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className="form-group">
          <label>Comentario/Pedido *</label>
          <textarea
            name="comentario"
            value={formData.comentario}
            onChange={handleChange}
            required
            placeholder="Comparte tu intención de oración..."
            maxLength="500"
          />
          <div className="char-counter">{formData.comentario.length}/500 caracteres</div>
          <div className="auto-category-note">
            La categoría se asignará automáticamente según el contenido de tu pedido
          </div>
        </div>

        <div className="form-group">
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="visible"
                checked={formData.visible}
                onChange={handleChange}
                className="checkbox-input"
              />
              <span className="checkbox-custom"></span>
              <span className="checkbox-text"> Acepto que mi pedido sea visible para la comunidad de oración</span>
            </label>
          </div>
        </div>

        <button 
          onClick={handleSubmit} 
          className={`submit-btn ${isSubmitting ? 'submitting' : ''}`}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="button-spinner"></span>
              Enviando pedido...
            </>
          ) : (
            'Enviar Pedido de Oración'
          )}
        </button>

        {!isAuthenticated && (
          <div className="new-prayer-login-hint">
            <button className="new-prayer-login-link" onClick={() => navigate('/login')}>
              Crea una cuenta
            </button>
            {' '}para enviar pedidos sin llenar el formulario cada vez
          </div>
        )}
      </div>

      {/* Modal de Suscripción */}
      {showSubscriptionModal && submittedPrayer && (
        <SubscriptionModal
          isOpen={showSubscriptionModal}
          onClose={handleCloseModal}
          prayerData={submittedPrayer}
          isReturningUser={isReturningUser}
          userStats={userStats}
        />
      )}

      {/* Modal de Bienvenida para Usuarios Suscritos */}
      {showWelcomeBackModal && submittedPrayer && (
        <WelcomeBackModal
          isOpen={showWelcomeBackModal}
          onClose={handleCloseWelcomeBackModal}
          prayerData={submittedPrayer}
          userStats={userStats}
          currentPreferences={subscriptionPreferences}
        />
      )}
    </div>
  );
}

export default NewPrayer;